import { dec, FuelDecimal, formatMoney } from './money.js';
export const CONTAINER_TYPES = ['BARREL', 'JERRYCAN'];
/** The deduction leaves nothing to pay for (fuel.md section 2). */
export class NonPositiveTotalError extends Error {
    constructor() {
        super('The final total after the jerrycan deduction must be greater than zero');
        this.name = 'NonPositiveTotalError';
    }
}
export class NoQuantityError extends Error {
    constructor() {
        super('A transaction needs a barrel or jerrycan quantity greater than zero');
        this.name = 'NoQuantityError';
    }
}
/**
 * The legacy formula shared by purchases, cash sales and quantity-based debt
 * sales (fuel.md section 2; legacy SaleController.php:101-136).
 *
 * @throws NoQuantityError when both barrels and jerrycans are <= 0, exactly as
 *   legacy.
 */
export function computeQuantityTotals(input) {
    let barrels = new FuelDecimal(0);
    let jerrycans = new FuelDecimal(0);
    for (const line of input.lines) {
        if (!Number.isInteger(line.qty) || line.qty < 0) {
            throw new RangeError(`Quantity must be a whole number >= 0, got ${line.qty}`);
        }
        if (line.qty === 0)
            continue;
        const amount = dec(line.fraction).times(line.qty);
        if (line.type === 'BARREL')
            barrels = barrels.plus(amount);
        else
            jerrycans = jerrycans.plus(amount);
    }
    if (barrels.lte(0) && jerrycans.lte(0))
        throw new NoQuantityError();
    const total = barrels
        .times(dec(input.barrelPrice))
        .plus(jerrycans.times(dec(input.jerrycanPrice)));
    const less = nonNegative(input.lessJerrycans ?? '0');
    const finalTotal = total.minus(less.times(dec(input.jerrycanPrice)));
    if (finalTotal.lte(0))
        throw new NonPositiveTotalError();
    return {
        barrels: barrels.toFixed(),
        jerrycans: jerrycans.minus(less).toFixed(),
        total: formatMoney(total),
        finalTotal: formatMoney(finalTotal),
    };
}
function nonNegative(value) {
    const result = dec(value);
    if (result.isNegative())
        throw new RangeError('The jerrycan deduction is typed without a sign (>= 0)');
    return result;
}
/**
 * The manual debt movement (legacy dashboard form, `is_dashboard = 1`): a
 * charge's final total is the typed amount minus the deducted jerrycans at
 * the jerrycan price; a repayment is the typed amount.
 */
export function computeManualCharge(amount, jerrycanPrice, lessJerrycans = '0') {
    const total = dec(amount);
    const finalTotal = total.minus(nonNegative(lessJerrycans).times(dec(jerrycanPrice)));
    if (finalTotal.lte(0))
        throw new NonPositiveTotalError();
    return { total: formatMoney(total), finalTotal: formatMoney(finalTotal) };
}
/** The receipt row «إلا جركل»: the deducted value, negative. */
export function lessJerrycansValue(lessJerrycans, jerrycanPrice) {
    return formatMoney(nonNegative(lessJerrycans).times(dec(jerrycanPrice)).negated());
}
/** A receipt line's value: qty × fraction × the price of its container type. */
export function lineValue(line, barrelPrice, jerrycanPrice) {
    const price = line.type === 'BARREL' ? barrelPrice : jerrycanPrice;
    return formatMoney(dec(line.fraction).times(line.qty).times(dec(price)));
}
/** List totals for quantities: the jerrycan total is net of the deducted jerrycans. */
export function quantityTotals(rows) {
    let barrels = new FuelDecimal(0);
    let jerrycans = new FuelDecimal(0);
    for (const row of rows) {
        for (const line of row.lines) {
            const amount = dec(line.fraction).times(line.qty);
            if (line.type === 'BARREL')
                barrels = barrels.plus(amount);
            else
                jerrycans = jerrycans.plus(amount);
        }
        jerrycans = jerrycans.minus(dec(row.lessJerrycans));
    }
    return { barrels: barrels.toFixed(), jerrycans: jerrycans.toFixed() };
}
//# sourceMappingURL=calculation.js.map